import { getDocumentStatus } from "./utils";
import { getPropertyAddress } from "./verifier";

/**
 * Owner portal documents grouping.
 *
 * This mirrors how the agency property screen presents the same data:
 * - lease paperwork lives on the Tenants tab, everything else on the Documents tab
 * - a document is only "in force" when it is flagged active AND has not expired,
 *   which is the rule behind the Active / Expiring Soon / Expired / Archive badges
 *
 * The `active` flag alone means "current record of its type", so grouping by it
 * would report expired contracts as active.
 */

export const DOCUMENT_PAGE_SIZE = 25;

// The types the agency property screen keeps out of its Documents tab and shows
// in the lease table instead.
export const LEASE_DOCUMENT_TYPES = ["lease", "extension", "termination", "rentModification"];

const IN_FORCE_STATUSES = ["active", "expiringSoon", "unknown"];

export function isLeaseDocument(document) {
	return LEASE_DOCUMENT_TYPES.includes(document?.type);
}

export function isInForce(document) {
	return IN_FORCE_STATUSES.includes(getDocumentStatus(document));
}

export function isExpiringSoon(document) {
	return getDocumentStatus(document) === "expiringSoon";
}

function timeValue(value) {
	if (!value) return 0;
	const parsed = Date.parse(value);
	return Number.isFinite(parsed) ? parsed : 0;
}

function byAddedDateDesc(a, b) {
	return timeValue(b?.addedDate) - timeValue(a?.addedDate);
}

// Soonest expiration first, so what needs renewing is at the top.
function byExpirationAsc(a, b) {
	const aValue = timeValue(a?.expirationDate);
	const bValue = timeValue(b?.expirationDate);
	if (aValue === bValue) return byAddedDateDesc(a, b);
	if (!aValue) return 1;
	if (!bValue) return -1;
	return aValue - bValue;
}

export function groupPropertyDocuments(property) {
	const currentLeases = [];
	const previousLeases = [];
	const currentDocuments = [];
	const pastDocuments = [];

	(property?.documents || []).forEach((document) => {
		const inForce = isInForce(document);
		if (isLeaseDocument(document)) {
			if (inForce) currentLeases.push(document);
			else previousLeases.push(document);
			return;
		}
		if (inForce) currentDocuments.push(document);
		else pastDocuments.push(document);
	});

	return {
		currentLeases: currentLeases.sort(byExpirationAsc),
		previousLeases: previousLeases.sort(byAddedDateDesc),
		currentDocuments: currentDocuments.sort(byExpirationAsc),
		pastDocuments: pastDocuments.sort(byAddedDateDesc),
		reports: [...(property?.reports || [])].sort(byAddedDateDesc),
	};
}

export function buildOwnerDocumentsView({ properties = [], query = "" } = {}) {
	const normalizedQuery = String(query || "")
		.trim()
		.toLocaleLowerCase("ro");

	const entries = (properties || []).map((property) => {
		const grouped = groupPropertyDocuments(property);
		const expiringSoonCount =
			grouped.currentLeases.filter(isExpiringSoon).length + grouped.currentDocuments.filter(isExpiringSoon).length;

		return {
			propertyId: property?.id,
			address: getPropertyAddress(property),
			...grouped,
			currentLeaseCount: grouped.currentLeases.length,
			leaseCount: grouped.currentLeases.length + grouped.previousLeases.length,
			documentCount: grouped.currentDocuments.length + grouped.pastDocuments.length,
			reportCount: grouped.reports.length,
			expiringSoonCount,
		};
	});

	entries.sort((a, b) => String(a.address).localeCompare(String(b.address), "ro", { sensitivity: "base" }));

	const visible = normalizedQuery
		? entries.filter((entry) => String(entry.address).toLocaleLowerCase("ro").includes(normalizedQuery))
		: entries;

	const sum = (key) => visible.reduce((total, entry) => total + entry[key], 0);

	return {
		properties: visible,
		propertyCount: entries.length,
		hiddenCount: entries.length - visible.length,
		currentLeaseCount: sum("currentLeaseCount"),
		documentCount: sum("documentCount"),
		reportCount: sum("reportCount"),
		expiringSoonCount: sum("expiringSoonCount"),
	};
}
